import React from 'react';

import { Box, Inline } from '@styleseat/ui';
import { useSelector } from 'react-redux';
import { BrandColor } from '@styleseat/brand';
import {
  Button,
  HeaderText,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
  PortalOverlayModal,
  Stack,
} from '../../../../ui';
import type { RootState } from '../../../../../store/models';

import { ProviderScheduleDay } from '../../../../../store/ProviderWorkSchedule.types';
import { ClientProviderProfile } from '../../../../../api/ProfileClientView';
import AboutTab from '../../AboutTab';
import AboutMe from '../../AboutMe';
import { DeprecatedAboutBusinessHours } from '../../BusinessHours';
import CancellationPolicy from '../../CancellationPolicy';
import { useIsMobile } from '../../../../../hooks';
import analytics from '../../../../../modules/analytics';
import { AboutProDepositPolicy } from '../../DepositPolicy';

type AboutMeModalProps = {
  profile: ClientProviderProfile;
  onMessagePro: () => void;
  onCallPro: () => void;
  onClose: () => void;
};

const AboutMeModal: React.FC<AboutMeModalProps> = ({
  onClose, profile, onMessagePro, onCallPro,
}) => {
  const isMobile = useIsMobile();

  const proSchedule = useSelector<RootState, ProviderScheduleDay[]>(
    state => state.providerWorkSchedule.schedules[profile.id],
  );
  const showCallButton = !!profile.public_phone_number;

  const handleOnMessagePro = () => {
    analytics.track('about-me-modal-on-message-pro-button-clicked', {
      pro_id: profile.id,
    });
    onMessagePro();
  };

  const handleOnCallPro = () => {
    analytics.track('about-me-modal-on-call-pro-button-clicked', {
      pro_id: profile.id,
    });
    onCallPro();
  };

  const handleOnMapPress = () => {
    analytics.track('about-me-modal-map-clicked', {
      pro_id: profile.id,
    });
  };

  return (
    <PortalOverlay
      name="about-me-modal"
      onBackdropClick={onClose}
      overflowScroll={false}
    >
      <PortalOverlayModal
        position="fullScreen"
        borderRadiusTop={isMobile ? 32 : 0}
        boxProps={{
          maxHeight: isMobile ? '100vh' : 600,
        }}
      >
        <PortalOverlayHeader
          hideBottomBorder
          renderRightAction={() => (
            <Box
              paddingRight={isMobile ? 24 : 16}
              paddingTop={isMobile ? 24 : 0}
            >
              <PortalOverlayHeaderCloseBtn
                noPadding
                onCloseRequest={onClose}
                color={BrandColor.Ash}
                testID="about_me_modal_close_button"
              />
            </Box>
          )}
        />
        <Stack
          testID="about_me_modal_about_information"
          flex={1}
          overflowY="scroll"
        >
          <Box
            paddingTop={24}
            paddingX={24}
            testID="about_me_modal_header"
          >
            <HeaderText
              level={1}
              testID="about_me_modal_header_text"
            >
              About
            </HeaderText>
          </Box>
          <Box testID="about_me_modal_about_information">
            <AboutTab
              showMap
              provider={profile}
              aboutMe={(
                <AboutMe
                  blurb={profile.blurb}
                  withHeader
                />
              )}
              locationAction={null}
              businessHours={<DeprecatedAboutBusinessHours schedule={proSchedule} />}
              cancellationPolicy={profile.cancellation_policy_text ? (
                <CancellationPolicy
                  cancellationPolicyText={profile.cancellation_policy_text}
                  providerName={profile.name}
                />
              ) : null}
              contactActions={(
                <Inline
                  space={16}
                >
                  <Button
                    onClick={handleOnMessagePro}
                    appearance="outline-thin"
                    size="medium"
                    testID="about_me_modal_message_button"
                  >
                    Message
                  </Button>
                  {showCallButton && (
                    <Button
                      onClick={handleOnCallPro}
                      appearance="outline-thin"
                      size="medium"
                      testID="about_me_modal_call_button"
                    >
                      Call
                    </Button>
                  )}
                </Inline>
              )}
              depositPolicy={profile.deposit_policy_text ? (
                <AboutProDepositPolicy
                  depositPolicyText={profile.deposit_policy_text}
                  providerName={profile.name}
                />
              ) : null}
              onExpandHighlight={() => {}}
              onCollapseHighlight={() => {}}
              onPressMap={handleOnMapPress}
              shouldShowSpecialties={false}
            />
          </Box>
        </Stack>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};
export default AboutMeModal;
